import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import CheckoutSheet from "@/components/CheckoutSheet";
import { type CartItem, type OrderMode, products as PRODUCTS } from "@/data/pricing";

interface CheckoutContextType {
  openCheckout: (cart: CartItem[], mode: OrderMode, totalPrice: number) => void;
  resumeCheckout: (order: ResumableOrder) => void;
  resumeOrderId: string | null;
  promoCode: string | null;
  clearPromoCode: () => void;
}

export interface ResumableOrder {
  id: string;
  order_mode: "purchase" | "subscription";
  total_cents: number | null;
  items: Array<{ product_id: string; quantity: number }> | null;
  shipping_address: {
    address: string;
    city: string;
    postalCode: string;
    country: string;
  } | null;
}

const CheckoutContext = createContext<CheckoutContextType>({
  openCheckout: () => {},
  resumeCheckout: () => {},
  resumeOrderId: null,
  promoCode: null,
  clearPromoCode: () => {},
});

export const useCheckout = () => useContext(CheckoutContext);

const PROMO_STORAGE_KEY = "aquadias_promo_code";

export const CheckoutProvider = ({ children }: { children: ReactNode }) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orderMode, setOrderMode] = useState<OrderMode>("purchase");
  const [totalPrice, setTotalPrice] = useState(0);
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [resumeOrderId, setResumeOrderId] = useState<string | null>(null);
  const [resumeShippingAddress, setResumeShippingAddress] = useState<
    ResumableOrder["shipping_address"] | null
  >(null);

  // Capture ?promo=CODE from URL on mount, persist in sessionStorage so it
  // survives navigation between landing page and checkout.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("promo");
      if (fromUrl) {
        const normalized = fromUrl.trim().toUpperCase().slice(0, 64);
        if (/^[A-Z0-9_-]+$/.test(normalized)) {
          sessionStorage.setItem(PROMO_STORAGE_KEY, normalized);
          setPromoCode(normalized);
          params.delete("promo");
          const newQuery = params.toString();
          const newUrl =
            window.location.pathname + (newQuery ? `?${newQuery}` : "") + window.location.hash;
          window.history.replaceState({}, "", newUrl);
          return;
        }
      }
      const stored = sessionStorage.getItem(PROMO_STORAGE_KEY);
      if (stored) setPromoCode(stored);
    } catch {
      // sessionStorage may be blocked — non-critical
    }
  }, []);

  const clearPromoCode = () => {
    try {
      sessionStorage.removeItem(PROMO_STORAGE_KEY);
    } catch {
      // ignore
    }
    setPromoCode(null);
  };

  const openCheckout = (items: CartItem[], mode: OrderMode, price: number) => {
    setResumeOrderId(null);
    setResumeShippingAddress(null);
    setCart(items);
    setOrderMode(mode);
    setTotalPrice(price);
    setSheetOpen(true);
  };

  // Resume an awaiting_payment order: rebuild the cart from saved items so
  // the Sheet can show the correct summary, and pass resumeOrderId so the
  // edge function reuses (not re-creates) the order row.
  const resumeCheckout = (order: ResumableOrder) => {
    const items: CartItem[] = (order.items ?? [])
      .map((it) => {
        const product = PRODUCTS.find((p) => p.id === it.product_id);
        if (!product) return null;
        return { product, quantity: it.quantity };
      })
      .filter((x): x is CartItem => x !== null);
    setResumeOrderId(order.id);
    setResumeShippingAddress(order.shipping_address ?? null);
    setCart(items);
    setOrderMode(order.order_mode);
    setTotalPrice((order.total_cents ?? 0) / 100);
    setSheetOpen(true);
  };

  return (
    <CheckoutContext.Provider value={{ openCheckout, resumeCheckout, resumeOrderId, promoCode, clearPromoCode }}>
      {children}
      <CheckoutSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) {
            setResumeOrderId(null);
            setResumeShippingAddress(null);
          }
        }}
        cart={cart}
        orderMode={orderMode}
        totalPrice={totalPrice}
        onOrderModeChange={(mode, price) => {
          setOrderMode(mode);
          setTotalPrice(price);
        }}
        resumeShippingAddress={resumeShippingAddress}
      />
    </CheckoutContext.Provider>
  );
};
